import { faPen, faPenToSquare } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AlertModal, ArrowIcon, Box, Flex, Text, TextButton } from '@modusign/design-system';
import { ChangeEvent, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useToggle } from 'react-use';

import DocumentHeaderDivider from '@app/components/DocumentHeaderDivider';
import HelpButton from '@app/components/HelpButton';
import { recordGTMEvent } from '@app/store/analytics/actions';

import DraftSaveButton from './DraftSaveButton';

export interface DraftEditorTopbarProps {
  name: string;
  isSubmitting: boolean;
  onExitClick: () => void;
  onToDraftsClick?: () => void;
  onDraftCreate: () => void;
  onDraftUpdate?: () => void;
  onTitleChange: (title: string) => void;
}

export default function DraftEditorTopbar({
  name: title,
  isSubmitting,
  onDraftCreate,
  onDraftUpdate,
  onExitClick,
  onToDraftsClick,
  onTitleChange,
}: DraftEditorTopbarProps) {
  const [isTitleInfoModalOpen, toggleTitleInfoModalOpen] = useToggle(false);

  const dispatch = useDispatch();

  const inputRef = useRef<HTMLInputElement>(null);

  const handleSaveClick = () => {
    if (onDraftUpdate) {
      onDraftUpdate();
    } else {
      onDraftCreate();
    }
  };

  return (
    <>
      <Flex
        bg="navigationBackgroundSecondaryDark"
        justifyContent="space-between"
        p={7}
        width="100%"
      >
        <Flex
          alignItems="center"
          as="button"
          color="textNeutralUltraLight"
          fontWeight="medium"
          sx={{
            ':hover': {
              color: 'white',
            },
          }}
          verticalAlign="middle"
          width="240px"
          onClick={onExitClick}
        >
          <ArrowIcon direction="left" mr={3} />
          나가기
        </Flex>
        <Flex alignItems="center" flexGrow={1} justifyContent="center" position="relative">
          <Box
            as="input"
            bg="transparent"
            color="white"
            fontSize="15px"
            maxLength={50}
            minLength={2}
            overflow="hidden"
            placeholder="서식 이름을 입력해주세요."
            ref={inputRef}
            sx={{
              textOverflow: 'ellipsis',
              '&:focus ~ div': {
                display: 'none',
              },
              '::placeholder': {
                color: 'textNeutralUltraLight',
              },
              '&:focus::placeholder': {
                color: 'transparent',
              },
            }}
            textAlign="center"
            value={title}
            whiteSpace="nowrap"
            width={title.length === 0 ? 'initial' : '100%'}
            onChange={(e: ChangeEvent<HTMLInputElement>) => onTitleChange(e.target.value)}
          />
          {title.length === 0 && (
            <Box data-testid="pen-icon" display="inline-block" width="28px">
              <FontAwesomeIcon color="#BABABA" icon={faPen} />
            </Box>
          )}
        </Flex>
        <Flex alignItems="center" justifyContent="flex-end" textAlign="right" width="250px">
          {onToDraftsClick && (
            <TextButton
              mr={6}
              sx={{
                ':hover, :focus > *': {
                  color: 'white',
                },
              }}
              textDecoration="plain"
              onClick={onToDraftsClick}
            >
              <FontAwesomeIcon color="#BABABA" icon={faPenToSquare} />
              <Text
                color="textNeutralUltraLight"
                fontWeight="medium"
                ml={2}
                onClick={() =>
                  dispatch(
                    recordGTMEvent('appDraftsClick', {
                      clickOn: '서식이용하기',
                      formName: title,
                    })
                  )
                }
              >
                작성서식함
              </Text>
            </TextButton>
          )}
          <DraftSaveButton
            disabled={(title.length > 0 && title.length < 2) || title.length > 50 || isSubmitting}
            onSave={() => {
              if (title.length === 0) {
                toggleTitleInfoModalOpen();
              } else {
                handleSaveClick();
              }
            }}
            onSaveAsNewDraft={onDraftUpdate ? onDraftCreate : undefined}
          />
          <Flex alignItems="center" height="100%" ml={3}>
            <DocumentHeaderDivider />
            <HelpButton variant="darkIcon" />
          </Flex>
        </Flex>
      </Flex>
      <AlertModal
        isOpen={isTitleInfoModalOpen}
        title="서식 저장 안내"
        variant="warning"
        onClose={() => {
          toggleTitleInfoModalOpen();
          inputRef.current?.focus();
        }}
      >
        먼저 서식 이름을 입력한 후 저장해 주세요.
      </AlertModal>
    </>
  );
}
